<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PlayerProfile, PlayerStatus } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NInput } from 'naive-ui';
import { Mail, Phone, Activity, HeartPulse, User, Gauge, Zap, Sparkles, Check } from 'lucide-vue-next';

interface Props {
  modelValue: boolean;
  player: PlayerProfile | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'update:player', player: PlayerProfile): void;
}>();

const { t } = useI18n();
const { notifySuccess } = useToastNotification();

const currentStatus = ref<PlayerStatus>('ACTIVE');
const coachNotes = ref<string>('');

watch(() => props.player, (p) => {
  if (p) {
    currentStatus.value = p.status;
    coachNotes.value = `Excellent plate discipline and strong two-strike approach. Key starter for ${p.graduationYear || 2026} showcase circuit.`;
  }
}, { immediate: true });

const handleStatusChange = (newStatus: PlayerStatus) => {
  if (!props.player) return;
  currentStatus.value = newStatus;
  const updated = { ...props.player, status: newStatus };
  emit('update:player', updated);
};

const handleSaveProfile = () => {
  if (!props.player) return;
  const updated = { ...props.player, status: currentStatus.value };
  emit('update:player', updated);
  notifySuccess(t('playerModal.profileSaved'), t('playerModal.profileSavedMsg'));
  emit('update:modelValue', false);
};
</script>

<template>
  <AppModal
    :model-value="modelValue"
    @update:model-value="(val) => emit('update:modelValue', val)"
    :title="t('playerModal.title')"
    :subtitle="player ? `#${player.jerseyNumber} ${player.firstName} ${player.lastName}` : ''"
    max-width="xl"
  >
    <div v-if="player" class="space-y-6">
      <!-- Player Header Card -->
      <div class="flex items-center gap-4 p-4 rounded-2xl bg-slate-950 border border-slate-800">
        <div class="w-16 h-16 rounded-2xl bg-gradient-to-tr from-sky-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg shadow-sky-500/20 shrink-0">
          #{{ player.jerseyNumber }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-bold text-slate-100 truncate">
              {{ player.firstName }} {{ player.lastName }}
            </h3>
            <AppBadge variant="position">{{ player.position }}</AppBadge>
          </div>
          <p class="text-xs text-slate-400 mt-0.5">
            {{ t('playerModal.bats') }}: <span class="text-slate-200 font-semibold">{{ player.bats }}</span> &bull;
            {{ t('playerModal.throws') }}: <span class="text-slate-200 font-semibold">{{ player.throws }}</span> &bull;
            {{ t('playerModal.gradClass', { year: player.graduationYear || 2026 }) }}
          </p>
        </div>
      </div>

      <!-- Quick Metrics Grid -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div class="p-3 bg-slate-950/70 rounded-xl border border-slate-800/80 text-center">
          <span class="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block">{{ t('playerModal.heightWeight') }}</span>
          <span class="text-xs font-bold text-slate-200 mt-1 block">
            {{ player.heightInches ? `${Math.floor(player.heightInches / 12)}'${player.heightInches % 12}"` : '6\'1"' }} / {{ player.weightLbs || 188 }} lbs
          </span>
        </div>
        <div class="p-3 bg-slate-950/70 rounded-xl border border-slate-800/80 text-center">
          <span class="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold block">{{ t('playerModal.exitVelocity') }}</span>
          <span class="text-xs font-bold text-emerald-300 font-mono mt-1 block">94.2 MPH</span>
        </div>
        <div class="p-3 bg-slate-950/70 rounded-xl border border-slate-800/80 text-center">
          <span class="text-[10px] text-sky-400 uppercase tracking-wider font-semibold block">{{ t('playerModal.armVelocity') }}</span>
          <span class="text-xs font-bold text-sky-300 font-mono mt-1 block">89.6 MPH</span>
        </div>
        <div class="p-3 bg-slate-950/70 rounded-xl border border-slate-800/80 text-center">
          <span class="text-[10px] text-amber-400 uppercase tracking-wider font-semibold block">{{ t('playerModal.sprint60') }}</span>
          <span class="text-xs font-bold text-amber-300 font-mono mt-1 block">6.68 s</span>
        </div>
      </div>

      <!-- Status Toggle Selector -->
      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-2">{{ t('playerModal.availability') }}</label>
        <div class="grid grid-cols-3 gap-2">
          <button
            type="button"
            @click="handleStatusChange('ACTIVE')"
            :class="[
              'px-3 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5',
              currentStatus === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm' : 'bg-slate-950 text-slate-400 border border-slate-800 hover:text-slate-200'
            ]"
          >
            <Activity class="w-3.5 h-3.5" />
            <span>{{ t('playerModal.active') }}</span>
          </button>
          <button
            type="button"
            @click="handleStatusChange('INJURED')"
            :class="[
              'px-3 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5',
              currentStatus === 'INJURED' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-sm' : 'bg-slate-950 text-slate-400 border border-slate-800 hover:text-slate-200'
            ]"
          >
            <HeartPulse class="w-3.5 h-3.5" />
            <span>{{ t('playerModal.injured') }}</span>
          </button>
          <button
            type="button"
            @click="handleStatusChange('RESERVE')"
            :class="[
              'px-3 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5',
              currentStatus === 'RESERVE' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm' : 'bg-slate-950 text-slate-400 border border-slate-800 hover:text-slate-200'
            ]"
          >
            <User class="w-3.5 h-3.5" />
            <span>{{ t('playerModal.reserve') }}</span>
          </button>
        </div>
      </div>

      <!-- Coach Notes -->
      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('playerModal.scoutingNotes') }}</label>
        <NInput
          v-model:value="coachNotes"
          type="textarea"
          :rows="2"
          placeholder="Enter scouting notes and performance feedback..."
        />
      </div>

      <!-- Contact Info -->
      <div class="space-y-1.5">
        <label class="block text-xs font-semibold text-slate-300">{{ t('playerModal.contacts') }}</label>
        <div class="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-2 text-xs">
          <div class="flex items-center gap-2 text-slate-300">
            <Mail class="w-4 h-4 text-slate-500" />
            <span>{{ player.email || t('playerModal.noEmail') }}</span>
          </div>
          <div class="flex items-center gap-2 text-slate-300">
            <Phone class="w-4 h-4 text-slate-500" />
            <span>{{ player.phone || '(555) 019-4829' }}</span>
          </div>
        </div>
      </div>

      <div class="pt-4 flex justify-end gap-3 border-t border-slate-800">
        <NButton
          quaternary
          @click="emit('update:modelValue', false)"
        >
          {{ t('common.cancel') }}
        </NButton>
        <NButton
          type="primary"
          @click="handleSaveProfile"
          class="font-bold text-xs"
        >
          {{ t('playerModal.saveChanges') }}
        </NButton>
      </div>
    </div>
  </AppModal>
</template>